import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { db } from '../firebase';

interface AdminConfig {
  baseFare: number;
  pricePerKm: number;
  extraStopFee: number;
  returnFee: number;
  longDistanceThresholdKm: number;
  longDistanceSurchargePercent: number;
  surgeMultiplier: number;
  surgeStartHour: string; // e.g. "17:00"
  surgeEndHour: string;   // e.g. "19:00"
}

const defaultConfig: AdminConfig = {
  baseFare: 15.00,
  pricePerKm: 2.50,
  extraStopFee: 5.00,
  returnFee: 10.00,
  longDistanceThresholdKm: 60,
  longDistanceSurchargePercent: 40,
  surgeMultiplier: 1.5,
  surgeStartHour: "17:00",
  surgeEndHour: "19:00",
};

interface AdminContextType {
  config: AdminConfig;
  updateConfig: (newConfig: Partial<AdminConfig>) => Promise<void>;
  isSurgeActive: boolean;
  configLoaded: boolean;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function AdminProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AdminConfig>(defaultConfig);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Lê do Realtime Database em Tempo Real (nó quoteConfig)
  useEffect(() => {
    if (!db) {
      // RTDB não disponível — usa defaults
      setConfigLoaded(true);
      return;
    }

    const configRef = ref(db, 'quoteConfig');
    const unsubscribe = onValue(
      configRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          // Garante que todos os campos numéricos sejam números (não strings)
          const parsed: AdminConfig = {
            baseFare: parseFloat(data.baseFare) || defaultConfig.baseFare,
            pricePerKm: parseFloat(data.pricePerKm) || defaultConfig.pricePerKm,
            extraStopFee: parseFloat(data.extraStopFee) || defaultConfig.extraStopFee,
            returnFee: parseFloat(data.returnFee) || defaultConfig.returnFee,
            longDistanceThresholdKm: parseFloat(data.longDistanceThresholdKm) || defaultConfig.longDistanceThresholdKm,
            longDistanceSurchargePercent: parseFloat(data.longDistanceSurchargePercent) || defaultConfig.longDistanceSurchargePercent,
            surgeMultiplier: parseFloat(data.surgeMultiplier) || defaultConfig.surgeMultiplier,
            surgeStartHour: data.surgeStartHour || defaultConfig.surgeStartHour,
            surgeEndHour: data.surgeEndHour || defaultConfig.surgeEndHour,
          };
          setConfig(parsed);
        }
        setConfigLoaded(true);
      },
      (_error) => {
        console.error('Erro ao ler quoteConfig do Realtime Database:', _error);
        setConfigLoaded(true); // Usa defaults se falhar
      }
    );

    return () => unsubscribe();
  }, []);

  const updateConfig = async (newConfig: Partial<AdminConfig>): Promise<void> => {
    const updated = { ...config, ...newConfig };
    setConfig(updated); // Atualização local otimista

    if (!db) {
      console.warn('Realtime Database não disponível — config salvo apenas localmente.');
      return;
    }

    try {
      const configRef = ref(db, 'quoteConfig');
      await set(configRef, updated);
    } catch (err) {
      console.error('Erro ao salvar quoteConfig no Realtime Database:', err);
      throw err; // Permite que o chamador exiba feedback ao usuário
    }
  };

  const checkSurge = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();

    const [startH, startM] = config.surgeStartHour.split(':').map(Number);
    const [endH, endM] = config.surgeEndHour.split(':').map(Number);

    const currentTime = currentHour * 60 + currentMinutes;
    const startTime = startH * 60 + (startM || 0);
    const endTime = endH * 60 + (endM || 0);

    return currentTime >= startTime && currentTime <= endTime;
  };

  const [isSurgeActive, setIsSurgeActive] = useState(checkSurge());

  useEffect(() => {
    const interval = setInterval(() => {
      setIsSurgeActive(checkSurge());
    }, 60000);
    return () => clearInterval(interval);
  }, [config.surgeStartHour, config.surgeEndHour]);

  return (
    <AdminContext.Provider value={{ config, updateConfig, isSurgeActive, configLoaded }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}
